import CheckIcon from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import { alpha, styled } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import React, { useState } from 'react';

const PREFIX = 'PeriodMenu';

const classes = {
  periodButton: `${PREFIX}-periodButton`,
  selected: `${PREFIX}-selected`,
  container: `${PREFIX}-container`,
  selectButton: `${PREFIX}-selectButton`,
  periodPopper: `${PREFIX}-periodPopper`,
  otherColumns: `${PREFIX}-otherColumns`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.periodButton}`]: {
    backgroundColor: '#fff',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: alpha(theme.palette.grey[900], 0.04)
    },
    '&:selected': {
      backgroundColor: alpha(theme.palette.grey[900], 0.94)
    }
  },

  [`& .${classes.selected}`]: {
    backgroundColor: `${theme.palette.primary.light}!important`
  },

  [`& .${classes.container}`]: {
    padding: '10px',
    maxHeight: '600px',
    overflowY: 'auto'
  },

  [`& .${classes.selectButton}`]: {
    whiteSpace: 'nowrap',
    textAlign: 'center',
    color: theme.palette.primary.contrastText,
    height: '100%',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.11)
    }
  },

  [`& .${classes.periodPopper}`]: {
    marginTop: '20px'
  },

  [`& .${classes.otherColumns}`]: {
    borderLeft: `1px solid ${theme.palette.grey[500]}`,
    paddingRight: theme.spacing(1)
  }
}));

interface PeriodMenuProps {
  latestYears?: number;
  periods: any;
  levels: any;
  selectedPeriod?: any;
  isLoading: boolean;
  handleSelect: any;
}

export interface Period {
  id: string;
  name: string;
  typename: string;
  periodleveltype: string;
  currentperiod: string;
  related: string;
  startdate: string;
  enddate: string;
  periodlevel: string;
  periodindex: string;
  level?: number;
}

interface PopulatedPeriod extends Period {
  periods: Period[];
}

const PeriodMenu = ({
  latestYears,
  periods,
  levels,
  selectedPeriod,
  isLoading,
  handleSelect
}: PeriodMenuProps): JSX.Element => {
  const [canApply, setCanApply] = useState(false);
  const [tempPeriod, setTempPeriod] = useState<Period>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popper' : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setTempPeriod(selectedPeriod);
  };

  const populatePeriodLevels = (): PopulatedPeriod[] => {
    const populatedPeriodLevels: PopulatedPeriod[] = [];

    levels.forEach((periodLevel: Period) => {
      const list = periods.filter((item: Period) =>
        periodLevel.related.split(',').includes(item.id)
      );
      populatedPeriodLevels.push({
        ...periodLevel,
        periods: list.map((item: Period) => {
          return { ...item, level: parseInt(periodLevel.periodleveltype, 10) };
        })
      });
    });

    return populatedPeriodLevels;
  };

  const populatedPerLevs = populatePeriodLevels();

  const drawPickerContent = (pickerData: PopulatedPeriod[]): JSX.Element => {
    if (pickerData.length === 0) return <Root />;

    const first = pickerData.shift();

    const firstColumn = first && (
      <Grid item key={first.id} sx={{ paddingRight: 1 }}>
        <Grid container direction="column" spacing={1}>
          {Array.isArray(first.periods) &&
            first.periods
              .reverse()
              .slice(0, latestYears || first.periods.length)
              .map((year: Period) => (
                <Grid item key={year.id}>
                  <ToggleButton
                    data-testid="year-level-button"
                    className={classes.periodButton}
                    classes={{ selected: classes.selected }}
                    size="small"
                    value="check"
                    selected={
                      tempPeriod &&
                      year.startdate <= tempPeriod?.startdate &&
                      year.enddate >= tempPeriod?.enddate
                    }
                    onChange={(): void => {
                      setCanApply(true);
                      setTempPeriod(year);
                    }}
                  >
                    <CheckIcon
                      fontSize="small"
                      color="primary"
                      style={{
                        visibility:
                          tempPeriod &&
                          year.startdate <= tempPeriod?.startdate &&
                          year.enddate >= tempPeriod?.enddate
                            ? 'visible'
                            : 'hidden'
                      }}
                    />
                    {year?.name}
                  </ToggleButton>
                </Grid>
              ))}
        </Grid>
      </Grid>
    );

    const otherColumns = pickerData.map((perLev: PopulatedPeriod) => (
      <Grid key={perLev.id} item className={classes.otherColumns}>
        <Grid
          style={{ maxWidth: '350px' }}
          container
          direction={perLev.periodleveltype === '500' ? 'row' : 'column'}
          spacing={1}
        >
          {Array.isArray(perLev.periods) &&
            perLev.periods
              .filter(
                (item: Period) =>
                  item?.startdate.substr(0, 4) === tempPeriod?.startdate.substr(0, 4)
              )
              .map((yearPart: Period) => (
                <Grid item key={yearPart.id} xs={perLev.periodleveltype === '500' ? 4 : false}>
                  <ToggleButton
                    data-testid="other-level-button"
                    className={classes.periodButton}
                    classes={{ selected: classes.selected }}
                    size="small"
                    value="check"
                    selected={tempPeriod && tempPeriod.id === yearPart.id}
                    onChange={(): void => {
                      setCanApply(true);
                      setTempPeriod(yearPart);
                    }}
                  >
                    <CheckIcon
                      fontSize="small"
                      color="primary"
                      style={{
                        visibility:
                          tempPeriod && tempPeriod.id === yearPart.id ? 'visible' : 'hidden'
                      }}
                    />
                    {yearPart?.name}
                  </ToggleButton>
                </Grid>
              ))}
        </Grid>
      </Grid>
    ));

    return (
      <>
        {firstColumn}
        {otherColumns}
      </>
    );
  };

  return (
    <Root>
      <IconButton
        data-testid="chevron-previous"
        disabled={!selectedPeriod || selectedPeriod.periodindex === '0' || periods.length === 0}
        aria-label="previous"
        color="inherit"
        className={classes.selectButton}
        onClick={(): void => {
          const previous = periods.filter(
            (period: Period) => period.periodlevel === selectedPeriod.periodlevel
          )[parseInt(selectedPeriod.periodindex, 10) - 1];
          handleSelect(previous);
        }}
        size="large"
      >
        <ChevronLeft />
      </IconButton>
      <Button
        data-testid="period-selector-button"
        variant="contained"
        color="primary"
        disableElevation
        onClick={handleClick}
        aria-describedby={id}
        className={classes.selectButton}
        disabled={isLoading || periods.length === 0}
      >
        {selectedPeriod?.name || (isLoading ? 'Loading...' : 'No periods found')}
      </Button>
      <IconButton
        data-testid="chevron-next"
        disabled={
          !selectedPeriod ||
          periods.length === 0 ||
          parseInt(selectedPeriod.periodindex, 10) ===
            periods.filter((period: Period) => period.periodlevel === selectedPeriod.periodlevel)
              .length -
              1
        }
        aria-label="next"
        color="inherit"
        className={classes.selectButton}
        onClick={(): void => {
          const next = periods.filter(
            (period: Period) => period.periodlevel === selectedPeriod.periodlevel
          )[parseInt(selectedPeriod.periodindex, 10) + 1];
          handleSelect(next);
        }}
        size="large"
      >
        <ChevronRight />
      </IconButton>
      <Popper
        id={id}
        open={open}
        anchorEl={anchorEl}
        // onResize={() => {}}
        // onResizeCapture={() => {}}
      >
        <Root>
          <Card className={classes.periodPopper}>
            <Grid container spacing={1} className={classes.container}>
              {selectedPeriod && drawPickerContent(populatedPerLevs)}
            </Grid>

            <Grid spacing={1} container justifyContent="flex-end" className={classes.container}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                    handleClick(event);
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  data-testid="apply-button"
                  variant="contained"
                  color="primary"
                  disabled={!canApply}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                    setCanApply(false);
                    handleClick(event);
                    handleSelect(tempPeriod);
                  }}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Root>
      </Popper>
    </Root>
  );
};

export default PeriodMenu;
